// @flow
import _ from 'lodash'
import React, { Fragment, useState } from 'react'
import Switcher from '../../controls/Switcher'
import { translations, translateFunc } from '../../../shared/localize'
import styled from 'styled-components'
import type { FbPlacements, OptionType } from '../../types'
import type { Node } from 'react'
import type { PlacementType } from '../../AudienceEdit/PlacementTypeSelect'

const t = translateFunc(translations.ad_edit.preview_options)

const SwitcherWrapper = styled.div`
  padding: 20px 0;
`

type Tag =
  | 'leadform'
  | 'product'
  | 'column'
  | 'multi'
  | 'promo'
  | 'promotion'
  | 'with_video'
  | 'carousel'
  | 'all'
  | 'vk'
  | 'target_mail'
  | 'facebook'
  | 'google'
  | 'lenta'
  | 'facebook_feed'
  | 'instagram_stream'
  | 'audience_network_classic'
  | 'facebook_right_hand_column'
  | 'other'

type Props = {|
  namesRenderer: (names: Array<string>) => Node,
  providers: Array<string>,
  fbPlacements: FbPlacements,
  maxPreviewTag: Tag,
  hideSwitcher?: boolean
|}

export function calculateTag(audienceType: string, placementType: PlacementType): Tag {
  if (audienceType === 'leadform') {
    return 'leadform'
  } else if (audienceType === 'product_retargeting') {
    return 'product'
  } else if (audienceType === 'posts_promotion') {
    return 'promotion'
  } else if (placementType === 'column') {
    return 'column'
  } else if (placementType === 'multi_format') {
    return 'multi'
  } else if (placementType === 'promo_post') {
    return 'promo'
  } else if (placementType === 'with_video') {
    return 'with_video'
  } else if (placementType === 'carousel') {
    return 'carousel'
  } else {
    return 'all'
  }
}

export default function TagSwitcher(props: Props) {
  const { providers, fbPlacements, maxPreviewTag } = props

  const [statePreviewTag, setStatePreviewTag] = useState(maxPreviewTag)

  const data = renderData(statePreviewTag, providers, fbPlacements, maxPreviewTag)

  return (
    <Fragment>
      {!props.hideSwitcher && (
        <SwitcherWrapper>
          <Switcher
            options={data.options}
            value={data.tag}
            onChange={(value) => setStatePreviewTag(value)}
          />
        </SwitcherWrapper>
      )}
      {props.namesRenderer(data.names)}
    </Fragment>
  )
}

function renderData(currentTag, providers, fbPlacements, maxPreviewTag) {
  const previewTag = _.isEmpty(byTag(currentTag, providers, fbPlacements, maxPreviewTag))
    ? maxPreviewTag
    : currentTag

  return {
    tag: previewTag,
    names: byTag(previewTag, providers, fbPlacements, maxPreviewTag),
    options: previewOptions(providers, fbPlacements, maxPreviewTag)
  }
}

function previewOptions(
  providers: Array<string>,
  fbPlacements: FbPlacements,
  maxPreviewTag: string
): Array<OptionType> {
  let options = [
    { name: t('all'), id: maxPreviewTag },
    { name: t('column'), id: 'column' },
    { name: t('lenta'), id: 'lenta' },
    { name: t('other'), id: 'other' },
    { name: t('google'), id: 'google' },
    { name: t('target_mail'), id: 'target_mail' },
    { name: t('vk'), id: 'vk' },
    { name: t('facebook'), id: 'facebook' }
  ]

  return _.filter(
    options,
    (option) => !_.isEmpty(byTag(option.id, providers, fbPlacements, maxPreviewTag))
  )
}

function byTag(
  tag: string,
  providers: Array<string>,
  fbPlacements: FbPlacements,
  maxPreviewTag: string
): Array<string> {
  return selectPreviews({
    providers: providers,
    tags: [maxPreviewTag, tag],
    fbPlacements: fbPlacements
  })
}

export function selectPreviews(filters: {
  providers: Array<string>,
  tags: Array<string>,
  fbPlacements: FbPlacements
}): Array<string> {
  const { providers, tags, fbPlacements } = filters
  let result = allPreviewNames()

  if (fbPlacements) result = filterByFbPlacementsReject(result, fbPlacements)
  if (providers) result = filterByProviders(result, providers)
  if (tags) result = filterByTags(result, tags)

  return _.map(result, (item) => item.name)
}

type PreviewInfo = {|
  name: string,
  tags: Array<Tag>
|}

function filterByProviders(
  result: Array<PreviewInfo>,
  providers: Array<string>
): Array<PreviewInfo> {
  return _.filter(result, (info) => _.intersection(info.tags, providers).length > 0)
}

function filterByFbPlacementsReject(
  result: Array<PreviewInfo>,
  fbPlacements: FbPlacements
): Array<PreviewInfo> {
  const mutatedPlacements: Array<string> = _.flatten(
    _.map(fbPlacements || {}, (placements, platform) =>
      _.map(placements, (placement) => `${platform}_${placement}`)
    )
  )

  return _.reject(result, (previewInfo) => {
    if (!_.includes(previewInfo.tags, 'facebook')) return false
    if (_.intersection(previewInfo.tags, mutatedPlacements).length === 0) return true

    return false
  })
}

function filterByTags(result: Array<PreviewInfo>, tags: Array<string>): Array<PreviewInfo> {
  return _.filter(result, (info) => _.difference(tags, info.tags).length === 0)
}

function allPreviewNames(): Array<PreviewInfo> {
  return [
    {
      name: 'vk_promopost',
      tags: ['all', 'multi', 'promo', 'vk', 'lenta', 'with_video']
    },
    {
      name: 'tm_banner',
      tags: ['all', 'multi', 'other', 'target_mail']
    },
    {
      name: 'multi_facebook_preview',
      tags: ['all', 'multi', 'promo', 'lenta', 'facebook', 'facebook_feed', 'with_video']
    },
    {
      name: 'instagram_preview',
      tags: ['all', 'multi', 'promo', 'lenta', 'facebook', 'instagram_stream']
    },
    {
      name: 'facebook_mobile',
      tags: ['all', 'multi', 'other', 'facebook', 'audience_network_classic', 'with_video']
    },
    {
      name: 'vk_preview',
      tags: ['all', 'column', 'vk']
    },
    {
      name: 'facebook_preview',
      tags: ['all', 'multi', 'column', 'facebook', 'facebook_right_hand_column', 'with_video']
    },
    {
      name: 'tm_lenta',
      tags: ['all', 'multi', 'promo', 'lenta', 'target_mail', 'with_video']
    },
    {
      name: 'google_preview',
      tags: ['all', 'multi', 'column', 'google', 'with_video']
    },
    {
      name: 'google_simple',
      tags: ['all', 'multi', 'promo', 'other', 'google', 'with_video']
    },
    {
      name: 'tm_preview_multi',
      tags: ['all', 'multi', 'target_mail']
    },
    {
      name: 'tm_preview',
      tags: ['column', 'target_mail']
    }
  ]
}
